<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { summarizeForUser, type NormalizedError } from '@/errors';
// By file rather than through `components/common/index.ts`: that barrel exports `Table.vue`, which
// imports this component, so going through it closes a cycle — and a cycle through a barrel of
// class definitions fails at module-evaluation time, not at the call.
import CustomButton from '@/components/common/CustomButton.vue';
import ButtonType from '@/components/common/interfaces/ButtonType';

/**
 * The inline tier: shown in place of the panel that failed, leaving the rest of the app usable.
 *
 * Purely presentational — it takes a `NormalizedError` and emits. `ErrorScreen` is the full-page
 * variant and shares this look, so the two never drift.
 */
const props = defineProps({
  error: {
    type: Object as PropType<NormalizedError>
  },
  compact: {
    type: Boolean,
    default: false
  },
  retrying: {
    type: Boolean,
    default: false
  }
});

const emits = defineEmits(['retry', 'report', 'reload']);

const { t: $t } = useI18n();

const summary = computed(() => props.error ? summarizeForUser(props.error) : undefined);

/**
 * Whether retrying this failure could plausibly work — `normalizeError` decides, and it says no for
 * a render error, a 403, a validation refusal.
 */
const canRetry = computed(() => props.error?.isRetryable !== false);
</script>

<template>
  <div class="error-state" :class="{ 'error-state--compact': compact }">
    <FontAwesomeIcon :icon="['far', 'exclamation-triangle']" class="error-state-icon" />
    <h2 class="error-state-title">{{ summary?.title ?? $t('error_generic_title') }}</h2>
    <p class="error-state-message">{{ summary?.message ?? $t('error_generic_message') }}</p>
    <p class="error-state-reference" v-if="summary">
      {{ $t('error_reference') }} <code>{{ summary.reference }}</code>
    </p>
    <div class="error-state-actions">
      <CustomButton :text="$t('error_retry')"
        v-if="canRetry"
        :loading="retrying"
        :type="ButtonType.OUTLINED"
        @onClick="emits('retry')"/>
      <!-- Only where retrying is not on offer. A render error leaves "Report a problem" as the sole
           action, and reporting a failure does not clear it — so the user reads the one screen in the
           app with no way off it. Reloading is the recovery there, and it takes the slot the retry
           button would have had rather than becoming a permanent third button everywhere. -->
      <CustomButton :text="$t('error_reload_page')"
        v-else
        :type="ButtonType.OUTLINED"
        @onClick="emits('reload')"/>
      <CustomButton :text="$t('error_report_problem')"
        v-if="error?.isReportable"
        :margin-left="12"
        :type="ButtonType.WITHOUT_OUTLINE"
        @onClick="emits('report')"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  padding: 24px;
  text-align: center;

  &--compact {
    min-height: 160px;
    padding: 16px;
  }

  &-icon {
    font-size: 28px;
    color: #6b7075;
    margin-bottom: 16px;
  }

  &-title {
    margin: 0;
    font-size: 18px;
    color: #272a2e;
  }

  &-message {
    margin: 8px 0 0;
    max-width: 46ch;
    font-size: 14px;
    color: #6b7075;
  }

  &-reference {
    margin: 12px 0 0;
    font-size: 12px;
    color: #6b7075;

    code {
      font-size: 12px;
      letter-spacing: 0.04em;
    }
  }

  &-actions {
    display: flex;
    margin-top: 20px;
  }
}
</style>
